import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button, Tooltip, Space } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  UndoOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import './RichTextEditor.css'; // We'll create this for basic styling

// Basic Toolbar Component
const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <Space className="tiptap-toolbar" wrap>
      <Tooltip title="Bold">
        <Button
          icon={<BoldOutlined />}
          onClick={() => editor.chain().focus().toggleBold().run()}
          type={editor.isActive('bold') ? 'primary' : 'default'}
          disabled={!editor.can().chain().focus().toggleBold().run()}
        />
      </Tooltip>
      <Tooltip title="Italic">
        <Button
          icon={<ItalicOutlined />}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          type={editor.isActive('italic') ? 'primary' : 'default'}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
        />
      </Tooltip>
      <Tooltip title="Bullet List">
         <Button
           icon={<UnorderedListOutlined />}
           onClick={() => editor.chain().focus().toggleBulletList().run()}
           type={editor.isActive('bulletList') ? 'primary' : 'default'}
         />
      </Tooltip>
       <Tooltip title="Numbered List">
         <Button
           icon={<OrderedListOutlined />}
           onClick={() => editor.chain().focus().toggleOrderedList().run()}
           type={editor.isActive('orderedList') ? 'primary' : 'default'}
         />
      </Tooltip>
       <Tooltip title="Undo">
         <Button
           icon={<UndoOutlined />}
           onClick={() => editor.chain().focus().undo().run()}
           disabled={!editor.can().chain().focus().undo().run()}
         />
      </Tooltip>
       <Tooltip title="Redo">
         <Button
           icon={<RedoOutlined />}
           onClick={() => editor.chain().focus().redo().run()}
           disabled={!editor.can().chain().focus().redo().run()}
         />
      </Tooltip>
    </Space>
  );
};

// Main Editor Component
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Configure extensions as needed, e.g., disable heading levels
        heading: false, // Disable headings for simple notes
        // Add other configurations if necessary
      }),
    ],
    content: value || '', // Initial content
    onUpdate: ({ editor }) => {
      // Get HTML content and pass it to the parent via onChange
      onChange(editor.getHTML());
    },
    editorProps: {
        attributes: {
          // Add Ant Design input styles for consistency
          class: 'ant-input tiptap-editor',
        },
      },
  });

  // Update editor content if the external value prop changes
  // Be careful with this to avoid infinite loops if onChange updates the value prop directly
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Use `setContent` with `emitUpdate: false` to prevent triggering onUpdate again
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);


  return (
    <div className="tiptap-container">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
};

export default RichTextEditor;
